// ============================================================================
// useTrendsData — Personal Finance V2 (Blueprint §5.2)
// Hook que consome postings anuais e calcula tendências:
//   - Agregação por mês (últimos 12 meses)
//   - Média, mínimo, máximo por categoria
//   - Variação % mês a mês
// Baseado no YNAB Reflect Tab (Spending Trends)
// ============================================================================

import { useMemo } from "react";
import { DEFAULT_CATEGORIES } from "../domain/categories.js";

const MONTHS = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

/** Gera array dos últimos N meses no formato YYYY-MM */
function getLastNMonths(n) {
  const result = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    result.push(`${y}-${m}`);
  }
  return result;
}

/** Retorna nome amigável do mês (ex: "Jul/2026") */
function monthLabel(ym) {
  const [y, m] = ym.split("-");
  const idx = parseInt(m, 10) - 1;
  return `${MONTHS[idx]}/${y}`;
}

export function useTrendsData(
  yearPostings,
  templates,
  categories = DEFAULT_CATEGORIES,
) {
  const trendsSummary = useMemo(() => {
    const last12 = getLastNMonths(12);

    // 1. Agrupar postings por mês
    const byMonth = {};
    last12.forEach((ym) => {
      byMonth[ym] = { total: 0, byGroup: {}, count: 0 };
      Object.keys(categories).forEach((k) => {
        byMonth[ym].byGroup[k] = 0;
      });
    });

    yearPostings.forEach((p) => {
      const ym = (p.date || "").substring(0, 7);
      if (!byMonth[ym]) return;
      if (p.group === "RECEITAS") return;
      const val = Number(p.value) || 0;
      byMonth[ym].total += val;
      byMonth[ym].count += 1;
      if (byMonth[ym].byGroup[p.group] !== undefined) {
        byMonth[ym].byGroup[p.group] += val;
      }
    });

    // 2. Calcular médias por categoria
    const averages = {};
    const totalsPerGroup = {};
    Object.keys(categories).forEach((k) => {
      totalsPerGroup[k] = [];
    });

    last12.forEach((ym) => {
      Object.keys(categories).forEach((k) => {
        totalsPerGroup[k].push(byMonth[ym].byGroup[k] || 0);
      });
    });

    Object.keys(categories).forEach((k) => {
      const vals = totalsPerGroup[k];
      const sum = vals.reduce((a, b) => a + b, 0);
      const avg = sum / vals.length;
      const min = Math.min(...vals);
      const max = Math.max(...vals);
      const recent = vals[vals.length - 1] || 0;
      const previous = vals[vals.length - 2] || 0;
      let trend = "stable";
      let momChange = 0;
      if (previous > 0) {
        momChange = Math.round(((recent - previous) / previous) * 100);
        if (momChange > 5) trend = "up";
        else if (momChange < -5) trend = "down";
      }
      const budget = categories[k]?.budget || 0;
      averages[k] = {
        avg: Math.round(avg * 100) / 100,
        min: Math.round(min * 100) / 100,
        max: Math.round(max * 100) / 100,
        total: Math.round(sum * 100) / 100,
        trend,
        momChange,
        budget,
        recent: Math.round(recent * 100) / 100,
      };
    });

    // 3. Totais gerais
    const totalExpenses = last12.reduce(
      (acc, ym) => acc + byMonth[ym].total,
      0,
    );
    const overAllAvg = totalExpenses / last12.length;

    // 4. Mês mais recente com dados
    const monthsWithData = last12
      .filter((ym) => byMonth[ym].count > 0)
      .map((ym) => ({
        label: monthLabel(ym),
        ym,
        total: byMonth[ym].total,
        byGroup: byMonth[ym].byGroup,
      }));

    return {
      byMonth,
      averages,
      monthsWithData,
      totalExpenses,
      overAllAvg,
      totalMonths: last12.length,
      last12Months: last12.map((ym) => ({
        label: monthLabel(ym),
        ym,
        total: byMonth[ym].total,
        byGroup: byMonth[ym].byGroup,
      })),
    };
  }, [yearPostings, categories]);

  return trendsSummary;
}
